import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { WS_COLOR, WS_FONT } from './tokens';

export interface PageHeaderProps {
  title: string;
  /** Record identifier shown in mono above the title, e.g. `OPP-0147`. */
  id?: string;
  /** One quiet line under the title: counts, scope, dates. */
  subtitle?: ReactNode;
  /** Status chips beside the subtitle. */
  meta?: ReactNode;
  /** Buttons. One filled primary at most; the rest tonal or text. */
  actions?: ReactNode;
}

/**
 * THE page heading (DESIGN.md). One treatment replaces the four the inventory found:
 * Admin's 26px title with plain subtitle, ERP's bordered card header, Inventory Registry's
 * ruled blue-grey title and Project Management's 32px title with grey description.
 *
 * It sits in a locked row: it never scrolls with the data. Breadcrumbs are deliberately not
 * here — the shell draws the trail once (docs/architecture/navigation-and-breadcrumbs.md).
 *
 * Width: the title block takes the remaining space and truncates nothing — a long title wraps
 * to a second line. Actions keep their size and wrap beneath the title when the block is
 * narrower than 320px.
 */
export function PageHeader({ title, id, subtitle, meta, actions }: PageHeaderProps) {
  return (
    // Wraps by its own width, not the window's: a header inside a narrow pane or dialog must
    // behave like a header on a narrow screen. The actions drop beneath the title block once
    // it can no longer keep 320px.
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: '16px 32px',
        flex: 'none',
        minWidth: 0,
      }}
    >
      <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
        {id ? (
          <Typography
            component="div"
            sx={{
              fontFamily: WS_FONT.mono,
              fontSize: 12,
              lineHeight: '16px',
              color: WS_COLOR.textMuted,
            }}
          >
            {id}
          </Typography>
        ) : null}
        <Typography variant="h1" component="h1" sx={{ overflowWrap: 'break-word' }}>
          {title}
        </Typography>
        {subtitle || meta ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={3}
            sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 1 }}
          >
            {subtitle ? (
              <Typography variant="body2" sx={{ color: WS_COLOR.textMuted }}>
                {subtitle}
              </Typography>
            ) : null}
            {meta}
          </Stack>
        ) : null}
      </Box>
      {actions ? (
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 2, flex: 'none' }}>
          {actions}
        </Stack>
      ) : null}
    </Box>
  );
}
